
import React from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ITeamNotification, ITeamWebsite, ITournamentEdition, OmneHostService, SanitizedResponse, TeamTypesValue, TournamentStatus } from "../../../../services";
import { BoolHelper, Classname, DateHelper, NoseurObject, Paginator, Scheme, StringHelper, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";
import { i18nManager } from "../i18n";

interface TournamentCardProps {
    t: ITournamentEdition;
}

function TournamentCard({ t }: TournamentCardProps) {
    const isActive = t.status !== TournamentStatus.ENDED;
    const prizePools = Object.entries(t.meta?.price_pool_amounts ?? {});

    return (
        <ReactRouterDom.NavLink to={`/to/${t.tournament_id}`} target="_blank"
            className={`${styles["unlinked"]} ${styles["tournament-card"]} ${isActive ? styles["active-card"] : ""}`}
        >
            <img className={styles["t-banner"]} src={t.banner_url} alt={t.name} loading="lazy" />

            <div className={styles["t-body"]}>
                <div className={styles["t-status-row"]}>
                    <img src={t.logo_url} alt={t.name} style={{ width: 25, height: 25, borderRadius: "100%", marginRight: 5 }} />
                    <span className={`${styles["t-status"]} ${isActive ? styles["t-active"] : styles["t-past"]}`}>
                        {isActive ? "● Active" : "✓ Past"}
                    </span>
                </div>

                <div className={styles["t-name"]}>{t.name}</div>
                <div style={{ color: "var(--muted)", fontSize: "0.82rem" }}>{Utils.fineDate(t.dates.tournament_start_date ? new Date(t.dates.tournament_start_date) : DateHelper.now())}</div>

                <div className={styles["t-meta-row"]}>
                    {(!!t.physical_address_location) && (<div className={styles["t-meta-item"]}>📍 <span>{t.physical_address_location}</span></div>)}
                    {(!!t.participant_members_count) && (<div className={styles["t-meta-item"]}>👤 <span>{StringHelper.toSaneSentenceFormat(Object.keys(TeamTypesValue)[t.participant_members_count] ?? t.participant_members_count)}</span></div>)}
                    {(!!t.meta.host_name) && (<div className={styles["t-meta-item"]}>🏟️ <span>{t.meta.host_name}</span></div>)}
                </div>

                {(!!prizePools.length) && (<>
                    <div className={styles["t-prize-lbl"]} style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>Prize Pool</div>
                    {prizePools.map(([curr, amount]) => {
                        return (<React.Fragment key={curr}>
                            <div className={styles["t-prize"]}>{i18nManager.queryCountryCurrency(curr, "currency_code", "currency_sign")} {StringHelper.toPresentableMoneyValue(amount)}</div>
                        </React.Fragment>);
                    })}
                </>)}
            </div>
        </ReactRouterDom.NavLink>
    );
}

interface ActivitiesProps {
    website: ITeamWebsite;
}

export function Activities(props: ActivitiesProps) {

    const omnehostService = OmneHostService.getInstance();
    const [pastTournaments, setPastTournaments] = React.useState<ApiResponseData<ITournamentEdition>>();
    const [teamNotification, setTeamNotification] = React.useState<ApiResponseData<ITeamNotification>>();
    const [activeTournaments, setActiveTournaments] = React.useState<ApiResponseData<ITournamentEdition>>();
    const { page, setPage, atpage, setAtpage, ptpage, setPtpage, section, setSection } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", atpage: "1", ptpage: "1", section: "tournaments" } });
    const sectionTypesMap: NoseurObject<string[]> = {
        members: ["MEMBER_JOINED", "MEMBER_LEFT", "TEAM_MEMBER_KICKED", "TEAM_DISBANDED"],
        timeline: ["CREATE_TEAM", "TEAM_DISBANDED", "TEAM_REACTIVATED", "TEAM_OWNERSHIP_CHANGED"],
        tournament_timeline: ["TEAM_TOURNAMENT_EDITION_REGISTRATION", "TEAM_TOURNAMENT_EDITION_WITHDRAWAL", "TEAM_TOURNAMENT_EDITION_REMOVAL", "TEAM_OWNERSHIP_CHANGED"],
    };

    React.useEffect(fetchInformation, [omnehostService, page, section]);
    React.useEffect(() => fetchTournaments("PAST"), [ptpage]);
    React.useEffect(() => fetchTournaments("ACTIVE"), [atpage]);

    return (<>
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>Team <em>Activities</em></h1>
            <p className={styles["sec-sub"]}>Tournaments, events and club milestones</p>

            <div className={styles["tabs"]}>
                {([["tournaments", "Tournaments"], ["tournament_timeline", "Tournament Timeline"], ["timeline", "Timeline"], ["members", "Members"], ["achievements", "Achievements"]] as [string, string][]).map(([v, l]) => (
                    <button
                        key={v}
                        className={`${styles["tab-btn"]} ${section === v ? styles["active"] : ""}`}
                        onClick={() => {
                            //setPage("0");
                            setSection(v);
                        }}>
                        {l}
                    </button>
                ))}
            </div>

            {section === "tournaments" && (
                <>
                    <div style={{ marginBottom: "1.5rem" }}>
                        <div className={styles["sec-line"]} style={{ width: "40px" }} />
                        <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.4rem", letterSpacing: "2px", color: "var(--green)", marginBottom: "0.25rem" }}>
                            ACTIVE TOURNAMENTS
                        </h3>
                        <p style={{ color: "var(--muted)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Currently participating</p>
                    </div>
                    {((!!activeTournaments?.content)
                        ? ((activeTournaments.content && activeTournaments.content?.length > 0)
                            ? (<div className={styles["tournament-grid"]} style={{ marginBottom: "3rem" }}>
                                {activeTournaments?.content?.map((t: ITournamentEdition) => (
                                    <TournamentCard key={t.external_id} t={t} />
                                ))}
                            </div>)
                            : (Utils.emptyState()))
                        : (Utils.loadingState()))}

                    <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50, marginBottom: 100, justifyContent: "flex-start" }} rowsPerPage={activeTournaments?.size} totalRecords={activeTournaments?.total_elements} initialPage={parseInt("" + atpage)} onPageChange={(e) => {
                        setAtpage("" + e.currentPage)
                    }} template={{
                        activePageLabel: () => "",
                        nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
                    }} />

                    <div style={{ marginBottom: "1.5rem" }}>
                        <div className={styles["sec-line"]} style={{ width: "40px" }} />
                        <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.4rem", letterSpacing: "2px", color: "var(--muted)", marginBottom: "0.25rem" }}>
                            PAST TOURNAMENTS
                        </h3>
                        <p style={{ color: "var(--muted)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Historical participation</p>
                    </div>
                    {((!!pastTournaments?.content)
                        ? ((pastTournaments.content && pastTournaments.content?.length > 0)
                            ? (<div className={styles["tournament-grid"]} style={{ marginBottom: "3rem" }}>
                                {pastTournaments?.content?.map((t: ITournamentEdition) => (
                                    <TournamentCard key={t.external_id} t={t} />
                                ))}
                            </div>)
                            : (Utils.emptyState()))
                        : (Utils.loadingState()))}

                    <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50, justifyContent: "flex-start" }} rowsPerPage={pastTournaments?.size} totalRecords={pastTournaments?.total_elements} initialPage={parseInt("" + ptpage)} onPageChange={(e) => {
                        setPtpage("" + e.currentPage)
                    }} template={{
                        activePageLabel: () => "",
                        nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                        pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
                    }} />
                </>
            )}

            {(BoolHelper.equalsAny(section, ["timeline", "tournament_timeline", "members", "achievements"])) && (<>
                {((!!teamNotification?.content)
                    ? ((teamNotification.content && teamNotification.content?.length > 0)
                        ? (<div className={styles["activity-feed"]}>
                            {teamNotification.content!.map((a) => (<div key={a.external_id} className={styles["activity-item"]}>
                                {(!!a.image_location) && (<img className={styles["activity-icon"]} alt={a.title} src={a.image_location} />)}
                                <div className={styles["activity-text"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(a.message) }} />
                                <div className={styles["activity-time"]}>{Utils.fineDate(new Date(a.created_at))}</div>
                            </div>))}
                        </div>)
                        : (Utils.emptyState()))
                    : (Utils.loadingState()))}

                <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={teamNotification?.size} totalRecords={teamNotification?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                    setPage("" + e.currentPage)
                }} template={{
                    activePageLabel: () => "",
                    nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                    previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                    lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                    firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                    pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
                }} />
            </>)}
        </div>
    </>);

    function fetchInformation() {
        setTeamNotification({});
        if (page === "0") return;

        omnehostService.queryTeamNotifications(props.website.team.public_id, {
            page,
            sort: "created_at,DESC",
            type: sectionTypesMap[section] ?? ["NONE"],
        }).then(({ apiResponse }: SanitizedResponse<ITeamNotification>) => {
            setTeamNotification(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function fetchTournaments(status: string) {
        omnehostService.queryTeamTournaments(props.website.team.public_id, {
            status,
            page: (status === "PAST" ? ptpage : atpage),
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEdition>) => {
            if (status === "PAST") {
                setPastTournaments(apiResponse.data!);
                return;
            }
            setActiveTournaments(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Activities;